import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Switch, TableColumnsType } from 'antd'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useQuestionnairesServiceGetQuestionnaireSharedListEndpointKey,
  useQuestionnairesServiceShareQuestionnaireEndpoint,
  useQuestionnairesServiceUnShareQuestionnaireEndpoint
} from 'src/api/queries'
import { notification, Text } from 'src/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

export const useConfig = () => {
  const { t, currentLang } = useLocales()
  const { id: personId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { mutate: share, isPending: isSharePending } =
    useQuestionnairesServiceShareQuestionnaireEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            useQuestionnairesServiceGetQuestionnaireSharedListEndpointKey
          ]
        })
      }
    })

  const { mutate: unShare, isPending: isUnSharePending } =
    useQuestionnairesServiceUnShareQuestionnaireEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            useQuestionnairesServiceGetQuestionnaireSharedListEndpointKey
          ]
        })
      }
    })

  const shareItem = (id: string) => {
    share({
      id: String(id),
      requestBody: {
        personId: String(personId)
      }
    })
  }

  const unShareItem = (id: string) => {
    unShare({
      id: String(id),
      requestBody: {
        personId: String(personId)
      }
    })
  }

  const onChangeStatus = (id: string, action: 'share' | 'unshare') => {
    const actions = {
      share: shareItem,
      unshare: unShareItem
    }

    actions[action](id)
  }

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'title',
        title: t('classifiers.title'),
        width: '60%',
        render: ({ name }) => <Text>{name?.[currentLang?.value]}</Text>
      },
      {
        key: 'questionsCount',
        title: t('questionnaires.questions_count'),
        dataIndex: 'questionsCount',
        width: '20%'
      },
      {
        key: 'status',
        title: t('form.status'),
        width: '10%',
        render: ({ id, isShared, questionsCount }) => {
          return (
            <Flex gap={16}>
              {isShared
                ? t('questionnaires.active')
                : t('questionnaires.inactive')}
              <Switch
                loading={isSharePending || isUnSharePending}
                checked={isShared}
                onChange={() => {
                  if (questionsCount === 0) {
                    return notification.error({
                      message: t(
                        'questionnaires.cannot_share_quiz_with_no_questions'
                      )
                    })
                  }

                  onChangeStatus(id, isShared ? 'unshare' : 'share')
                }}
              />
            </Flex>
          )
        }
      },
      {
        key: 'see_details',
        title: t('common.see_details'),
        render: ({ id }) => (
          <Button
            type="link"
            onClick={() =>
              navigate(paths.patients.quiz.details(String(personId), id))
            }
          >
            {t('common.see_details')}
          </Button>
        )
      }
    ],
    [t]
  )

  return { columns }
}
